import { useState, useEffect } from 'react';
import { Stack, Text, Button, Paper, Group, Center } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Patient, Encounter, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import {
  PhysicalGrowthListItem,
  PhysicalGrowthAction,
} from './PhysicalGrowthListItem';
import { PhysicalGrowthInputModal } from './PhysicalGrowthInputModal';
import { PhysicalGrowthPrevious } from '@/components/inputs/PhysicalGrowthPrevious';
import type { PhysicalGrowthData } from '@/components/inputs/PhysicalGrowthInput';
import {
  savePhysicalGrowth,
  loadEncounterPhysicalGrowth,
  deletePhysicalGrowth,
  loadPreviousEncounterPhysicalGrowth,
} from '@/pages/ainigma/patient/examination/examination.helpers';
import { notifications } from '@mantine/notifications';

export interface PhysicalGrowthListProps {
  data: PhysicalGrowthData | null;
  onChange: (data: PhysicalGrowthData | null) => void;
  patient: Patient;
  encounter: Encounter;
  readonly?: boolean;
}

export const PhysicalGrowthList = ({
  data,
  onChange,
  patient,
  encounter,
  readonly = false,
}: PhysicalGrowthListProps) => {
  const { t } = useTranslation(['patient-pages']);
  const medplum = useMedplum();
  const profile = useMedplumProfile();

  // Modal states
  const [modalOpened, setModalOpened] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');

  // Previous encounter data
  const [previousData, setPreviousData] = useState<{
    data: PhysicalGrowthData | null;
    encounterDate?: string;
  } | null>(null);
  const [loadingPrevious, setLoadingPrevious] = useState(false);

  // Check if we have any data
  const hasData =
    data &&
    (data.value ||
      data.comments ||
      data.weight ||
      data.height ||
      data.headCircumference);

  // Load previous encounter data
  useEffect(() => {
    if (!patient?.id || !encounter?.id) {
      setPreviousData(null);
      return;
    }

    async function loadPrevious() {
      setLoadingPrevious(true);
      try {
        const result = await loadPreviousEncounterPhysicalGrowth(
          medplum,
          patient as Patient,
          encounter as Encounter
        );
        setPreviousData(result);
      } catch (error) {
        console.error('Error loading previous physical growth:', error);
        setPreviousData(null);
      } finally {
        setLoadingPrevious(false);
      }
    }

    loadPrevious();
  }, [medplum, patient, encounter]);

  // Handlers
  const handleAdd = () => {
    setModalMode('add');
    setModalOpened(true);
  };

  const handleAction = async (action: PhysicalGrowthAction) => {
    if (action === 'edit') {
      setModalMode('edit');
      setModalOpened(true);
      return;
    }

    if (action === 'remove') {
      try {
        // Delete from server immediately
        await deletePhysicalGrowth(medplum, patient, encounter);

        // Update local state
        onChange(null);

        // Show success notification
        notifications.show({
          color: 'green',
          title: t('physicalGrowth.deleteSuccess', {
            ns: 'patient-pages',
            defaultValue: 'Deleted',
          }),
          message: t('physicalGrowth.deleteSuccessMessage', {
            ns: 'patient-pages',
            defaultValue: 'Physical growth measurement deleted successfully',
          }),
        });
      } catch (error) {
        console.error('Error deleting physical growth:', error);
        notifications.show({
          color: 'red',
          title: t('physicalGrowth.deleteError', {
            ns: 'patient-pages',
            defaultValue: 'Error',
          }),
          message: t('physicalGrowth.deleteErrorMessage', {
            ns: 'patient-pages',
            defaultValue: 'Failed to delete physical growth measurement',
          }),
        });
      }
    }
  };

  const handleSave = async (savedData: PhysicalGrowthData) => {
    // Save immediately to the server
    await savePhysicalGrowth(
      medplum,
      patient,
      encounter,
      savedData,
      profile as Practitioner | undefined
    );

    // Invalidate cache to force fresh fetches (standard pattern in codebase)
    medplum.invalidateSearches('Observation');
  };

  const handleSaveSuccess = async (savedData: PhysicalGrowthData) => {
    // Reload the data from the server to get the updated observations with IDs
    try {
      const { data: reloadedData } = await loadEncounterPhysicalGrowth(
        medplum,
        patient,
        encounter
      );
      onChange(reloadedData);

      // Show success notification
      notifications.show({
        color: 'green',
        title: t('physicalGrowth.saveSuccess', {
          ns: 'patient-pages',
          defaultValue: 'Saved',
        }),
        message: t('physicalGrowth.saveSuccessMessage', {
          ns: 'patient-pages',
          defaultValue: 'Physical growth measurement saved successfully',
        }),
      });
    } catch (error) {
      console.error('Error reloading physical growth:', error);
      // Even if reload fails, update with the saved data
      onChange(savedData);
    }
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Text size="xl" fw={500}>
        {t('physicalGrowth.title', {
          ns: 'patient-pages',
          defaultValue: 'Physical Growth',
        })}
      </Text>

      {/* Main content area with previous encounter on the right */}
      <Group align="stretch" gap="lg" wrap="nowrap">
        {/* Left column - Current measurement or empty state */}
        <Stack gap="md" flex={1}>
          {!hasData ? (
            <>
              <Paper withBorder radius="md" p="xl" bg="gray.0" flex={1}>
                <Center h="100%">
                  <Text size="sm" c="dimmed" ta="center">
                    {t('physicalGrowth.noMeasurement', {
                      ns: 'patient-pages',
                      defaultValue: 'No physical growth measurement recorded',
                    })}
                  </Text>
                </Center>
              </Paper>
              {/* Edit button */}
              {!readonly && (
                <Button
                  leftSection={<IconPlus size={16} />}
                  variant="light"
                  size="sm"
                  onClick={handleAdd}
                >
                  {t('physicalGrowth.addMeasurement', {
                    ns: 'patient-pages',
                    defaultValue: 'Add Measurement',
                  })}
                </Button>
              )}
            </>
          ) : (
            <PhysicalGrowthListItem
              data={data}
              onAction={handleAction}
              isDeleting={false}
              readonly={readonly}
            />
          )}
        </Stack>

        {/* Right column - Previous encounter data */}
        {!loadingPrevious && (
          <PhysicalGrowthPrevious
            data={previousData?.data || null}
            encounterDate={previousData?.encounterDate}
          />
        )}
      </Group>

      {/* Modal */}
      <PhysicalGrowthInputModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onSave={handleSave}
        onSaveSuccess={handleSaveSuccess}
        data={hasData ? data : undefined}
        mode={modalMode}
      />
    </Stack>
  );
};
